import React, {useContext, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import AddWord from '../../components/AddWord';
import {
    addWord,
    removeWord,
    enterIdleMode,
    markWordAsFound,
    markLettersAsUsed,
    enterGameCompletedMode,
} from '../../actions/actions';
import {ANSWERING_GAME_MODE, EDITING_GAME_MODE} from '../../constants';
import Delete from '@material-ui/icons/Delete';
import styled from 'styled-components';
import {StateContext} from '../../App';

const StyledWordsPanel = styled('div')`
    grid-area: wordspanel;
    width: 100%;
    list-style: none;
    overflow: auto;
`;

const StyledWordListItem = styled('li')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 6px;
    margin-bottom: 6px;
    border: 1px solid lightblue;
    border-radius: 5px;
    text-transform: uppercase;
    color: #53a7ea;
    height: 30px;

    span {
        pointer-events: none;
        line-height: 21px;
        user-select: none;
    }

    input {
        line-height: 21px;
        width: 80%;
        border: none;
    }

    button {
        cursor: pointer;
        background-color: transparent;
        margin: 0;
        text-align: center;
        text-decoration: none;
        display: inline-block;
        border: none;
        color: #53a7ea;
        &:disabled {
            color: lightgray;
            cursor: initial;
        }
    }

    ${(props) =>
        props.isFound &&
        `
            background-color: grey;
            pointer-events: none;
            color: white;
        `}

    ${(props) =>
        props.gameMode === ANSWERING_GAME_MODE &&
        `&:hover {
            background-color: #53a7ea;
            color: white;
        }`}
`;

const propTypes = {
    words: PropTypes.array,
    onWordRemove: PropTypes.func,
    onWordAdd: PropTypes.func,
};

const WordsPanel = () => {
    const stateContext = useContext(StateContext);
    const words = useSelector((state) => state.words);
    const gameMode = useSelector((state) => state.gameMode);
    const collectedLetters = useSelector((state) => state.collectedLetters);
    const dispatch = useDispatch();

    useEffect(() => {
        // Check if all the words were found and if so
        // announce the end of the game
        const foundWords = words.filter((word) => word.isFound);
        const areAllWordsFound = words.length === foundWords.length;

        areAllWordsFound && dispatch(enterGameCompletedMode());
    }, [words, dispatch]);

    // TODO: Extract to a service
    function onWordItemMouseUp(event) {
        if (gameMode === ANSWERING_GAME_MODE) {
            event.stopPropagation();
            const question = event.target.dataset.word;
            const answer = collectedLetters.map((letter) => letter.value).join('');
            if (question === answer) {
                stateContext.addPoint();
                dispatch(markWordAsFound(question));
                const letterIds = collectedLetters.map((letter) => letter.id);
                dispatch(markLettersAsUsed(letterIds));
            }

            // Exit the answering game mode
            dispatch(enterIdleMode());
        }
    }

    return (
        <StyledWordsPanel>
            <ul>
                {words.map((word, i) => (
                    <StyledWordListItem
                        key={i}
                        onMouseUp={word.isFound ? null : onWordItemMouseUp}
                        data-word={word.text}
                        gameMode={gameMode}
                        isFound={word.isFound}
                    >
                        <span>{word.text}</span>
                        {gameMode === EDITING_GAME_MODE ? (
                            <button
                                onClick={() => {
                                    dispatch(removeWord(i));
                                }}
                            >
                                <Delete />
                            </button>
                        ) : null}
                    </StyledWordListItem>
                ))}

                {gameMode === EDITING_GAME_MODE ? (
                    <StyledWordListItem key="new">
                        <AddWord onWordAdd={(newWord) => dispatch(addWord(newWord))} />
                    </StyledWordListItem>
                ) : null}
            </ul>
        </StyledWordsPanel>
    );
};

WordsPanel.propTypes = propTypes;

export default WordsPanel;
